/**
 *
 *  DialingCodeSelectorPopup
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useCallback, useState, useRef } from "react";
import Transition, {
  ENTERING,
  ENTERED,
  EXITING,
  EXITED,
} from "react-transition-group/Transition";

// Utils
import { isAndroidDevice, isMac, isIosDevice } from "../../../utils";
import { COUNTRY_PHONE_CODES_WITH_FLAGS } from "../../../constants";

// Icons
import CloseIcon from "../../../icons/CloseIcon";

const transitionClasses = {
  [ENTERED]: "transition-entered",
  [ENTERING]: "transition-entering",
  [EXITING]: "transition-exiting",
  [EXITED]: "transition-exited",
};

const ListItem = ({ countryInfo, selected, onSelect }) => {
  const handleSelect = useCallback(() => {
    onSelect(countryInfo);
  }, [countryInfo, onSelect]);

  return (
    <div
      className="tbk-relative tbk-flex tbk-cursor-pointer tbk-select-none tbk-items-center tbk-border-b tbk-border-solid tbk-border-blue-grey-light tbk-py-2 tbk-pr-2"
      onClick={handleSelect}
    >
      <div className="tbk-absolute tbk-mr-1 tbk-text-3xl">
        {isAndroidDevice || isMac || isIosDevice ? (
          countryInfo.emoji
        ) : (
          <img src={countryInfo.image} className="tbk-w-4" />
        )}
      </div>
      <div
        className={`tbk-text-main tbk-grow tbk-pl-5 ${
          selected ? "tbk-text-coral" : "tbk-text-blue-dark"
        }`}
      >
        {countryInfo.name}
      </div>
      <div
        className={`tbk-text-main tbk-mr-1 ${
          selected ? "tbk-text-coral" : "tbk-text-blue-dark"
        }`}
      >
        {countryInfo.phoneCode}
      </div>
    </div>
  );
};

export default function DialingCodeSelectorPopup({
  show,
  selectedCountry,
  phoneCodeInputRef,
  onSelect,
  onClose,
}) {
  const [isScrollReady, setIsScrollReady] = useState(false);
  const listRef = useRef();
  const showRef = useRef(false);
  const popupRef = useRef(null);
  const onCloseRef = useRef();

  useEffect(() => {
    COUNTRY_PHONE_CODES_WITH_FLAGS.forEach((v, index) => {
      if (v.code === selectedCountry) {
        listRef.current.scrollTop =
          index * 59 - (listRef.current.offsetHeight / 2 - 30);

        setIsScrollReady(true);
      }
    });
  }, [selectedCountry, show]);

  useEffect(() => {
    if (!show) {
      setIsScrollReady(false);
    }
    showRef.current = show;
  }, [show]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const _onWindowClick = useCallback((e) => {
    const popupElement = popupRef.current;
    const isOpen = showRef.current;

    if (
      e.target !== popupElement &&
      !popupElement.contains(e.target) &&
      e.target !== phoneCodeInputRef.current &&
      !phoneCodeInputRef.current.contains(e.target) &&
      isOpen
    ) {
      showRef.current = false;
      onCloseRef.current && onCloseRef.current();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("click", _onWindowClick);
    window.addEventListener("touchstart", _onWindowClick);

    return () => {
      window.removeEventListener("click", _onWindowClick);
      window.removeEventListener("touchstart", _onWindowClick);
    };
  }, []);

  const handleListItemSelect = (value) => {
    onSelect(value);
  };

  return (
    <Transition
      nodeRef={popupRef}
      timeout={300}
      in={show}
      // addEndListener={this.handleTransitionEnd}
    >
      {(status, innerProps) => (
        <div
          {...innerProps}
          ref={popupRef}
          className={`tbk-absolute tbk--top-8 tbk-z-1 tbk-h-full tbk-w-96 tbk-overflow-hidden tbk-rounded-lg tbk-bg-basic-white tbk-p-2 tbk-pr-1 tbk-shadow-trube tbk-transition-all tbk-duration-300 ${
            status === EXITING || status === EXITED
              ? "tbk-pointer-events-none tbk-translate-y-1 tbk-opacity-0"
              : "tbk-translate-y-0 tbk-opacity-100"
          } ${transitionClasses[status]}`}
          style={{
            maxWidth: "100%",
            maxHeight: "494px",
            height: "calc(100vh - 120px)",
            visibility: isScrollReady ? "visible" : "hidden",
          }}
        >
          <button
            className="tbk-absolute tbk-right-3 tbk-text-coral"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-h3-subtitle tbk-mb-4 tbk-text-blue-grey">
            Choose dialing code:
          </h3>
          <div
            ref={listRef}
            className="tbk-overflow-y-scroll"
            style={{ height: "calc(100% - 66px)" }}
          >
            <div className="tbk-max-h-96">
              {show &&
                COUNTRY_PHONE_CODES_WITH_FLAGS.map((countryInfo) => (
                  <ListItem
                    key={countryInfo.code}
                    countryInfo={countryInfo}
                    selected={selectedCountry === countryInfo.code}
                    onSelect={handleListItemSelect}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </Transition>
  );
}
